import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import SoftwareCard from "@/components/site/SoftwareCard";
import { Loading, Empty, Pagination } from "@/components/site/Ui";
import { useApi, PRICING_MODELS, USE_CASES } from "@/lib/api";
import { useSite } from "@/lib/site";

const Select = ({ label, value, onChange, options, testId }) => (
  <label className="block">
    <span className="label !text-xs !text-[#64728A] uppercase tracking-wider">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="input" data-testid={testId}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);

export default function Directory({ fixedCategory }) {
  const [params, setParams] = useSearchParams();
  const { categories } = useSite();
  const get = (k, d = "") => params.get(k) || d;
  const [q, setQ] = useState(get("q"));
  useEffect(() => { setQ(get("q")); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    v ? next.set(k, v) : next.delete(k);
    if (k !== "page") next.delete("page");
    return next;
  }, { replace: true });
  const category = fixedCategory || get("category");
  const page = Number(get("page", "1"));
  const qs = new URLSearchParams({ q: get("q"), category, pricing: get("pricing"), use_case: get("use_case"), sort: get("sort", "name"), page, limit: 12 });
  const { data, loading, error } = useApi(`/software?${qs}`);
  const active = get("q") || get("pricing") || get("use_case") || (!fixedCategory && get("category"));

  return (
    <div data-testid="software-directory" className="grid lg:grid-cols-[260px_1fr] gap-8">
      <aside className="space-y-4 lg:sticky lg:top-24 self-start card p-5" data-testid="directory-filters">
        <form role="search" onSubmit={(e) => { e.preventDefault(); set("q", q.trim()); }}>
          <span className="label !text-xs !text-[#64728A] uppercase tracking-wider">Search</span>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A96A8]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or keyword" className="input !pl-9" data-testid="directory-search-input" aria-label="Search software" />
          </div>
          <button className="btn-primary btn-sm w-full mt-2" data-testid="directory-search-button">Search</button>
        </form>
        {!fixedCategory && (
          <Select label="Category" value={category} onChange={(v) => set("category", v)} testId="directory-category-select"
            options={[["", "All categories"], ...categories.map((c) => [c.slug, c.name])]} />
        )}
        <Select label="Pricing model" value={get("pricing")} onChange={(v) => set("pricing", v)} testId="directory-pricing-select"
          options={[["", "Any pricing"], ...PRICING_MODELS.map((p) => [p.value, p.label])]} />
        <Select label="Business use case" value={get("use_case")} onChange={(v) => set("use_case", v)} testId="directory-usecase-select"
          options={[["", "Any use case"], ...USE_CASES.map((u) => [u, u])]} />
        {active && (
          <button className="btn-ghost btn-sm w-full" onClick={() => setParams(new URLSearchParams(), { replace: true })} data-testid="directory-clear-filters">
            <X className="h-3.5 w-3.5" /> Clear filters
          </button>
        )}
      </aside>
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <p className="text-sm text-[#64728A]" data-testid="directory-result-count">{data ? `${data.total} ${data.total === 1 ? "result" : "results"}` : " "}</p>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-[#64728A]">Sort</span>
            <select value={get("sort", "name")} onChange={(e) => set("sort", e.target.value)} className="input !w-auto !py-2" data-testid="directory-sort-select">
              <option value="name">Name (A–Z)</option>
              <option value="name_desc">Name (Z–A)</option>
              <option value="newest">Newest published</option>
              <option value="oldest">Oldest published</option>
            </select>
          </label>
        </div>
        {loading ? <Loading /> : error ? <Empty title="Couldn't load software">Please refresh the page.</Empty> : data.items.length === 0 ? (
          <Empty title="No software matches your filters">Try a different keyword or clear the filters.</Empty>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2">{data.items.map((s) => <SoftwareCard key={s.slug} s={s} />)}</div>
            <Pagination page={data.page} pages={data.pages} onChange={(p) => { set("page", String(p)); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
          </>
        )}
      </div>
    </div>
  );
}
